import { useCallback, useState, useEffect, useMemo } from "react";

import mainStyle from "./ssid.module.scss";
import { isNil, size } from "lodash";

import { MenuSelect } from "components/Select/menuSelect";
import CollapseCard from "components/Collapse";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidWarnButton } from "components/buttons/SolidWarnButton";
import ModalContainer from "components/ModalContainer";

import { ReactComponent as SwitchOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as SwitchOffIcon } from "assets/images/icons/Toggle-Off.svg";
import { ReactComponent as UnselectedIcon } from "assets/images/icons/icn_unselected.svg";
import { ReactComponent as SelectedIcon } from "assets/images/icons/icn_selected.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show_hover.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_table_cell_Bin.svg";
import { ReactComponent as BinHoverIcon } from "assets/images/icons/icn_table_cell_Bin_hover.svg";
import { ReactComponent as CheckDisabledIcon } from "assets/images/icons/checkbox_disabled.svg";
import icon_snmp from 'assets/images/icons/icn_Info_solid.svg';
import icon_snmp_hover from 'assets/images/icons/icn_info_solid_hover.svg';

// Global Const
import { formLimitation, passwordRegex } from 'const';

// Const
import { SECURITY_ALLOWED_6G, SECURITY_ALLOWED_MLO, PMF_MAP } from "pages/System/RealmSetting/components/Wireless/SSID/const";

import {
  Box,
  Button,
  Typography,
  TextField,
  Tooltip,
  Checkbox,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

const SettingsDrawer = ({
  setAlertInfo,
  wirelessData,
  setWirelessData,
  onUpdateBlockerData,
  bandOptions,
  formData,
  setFormData,
  securityTypeAssembleList,
  securityTypeConfig,
  radiusProfileOptions,
  originalRowData,
  setDrawerOpen,
  isValidInputStyles,
  showEditGeneral,
  captivePortalOptions,
  bandCount,
  setBandCount,
}) => {
  const [securityList, setSecurityList] = useState(structuredClone(securityTypeAssembleList));
  const [hasDuplicateName, setHasDuplicateName] = useState(false);
  const [isStatusSwitched, setIsStatusSwitched] = useState(false);
  const [isSSIDBroadcastSwitched, setIsSSIDBroadcastSwitched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [removeModal, setRemoveModal] = useState(false);
  const [disableWifiModal, setDisableWifiModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const ssidNameSize = useMemo(() => size(formData?.ssid?.trim()), [size, formData?.ssid]);
  const theme = useTheme();

  const rowStyle = {
    display: "flex",
    gap: 3,
    alignItems: "center",
    width: "100%",
  };

  const labelStyle = {
    width: '160px',
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[0],
  };

  const textfieldStyle = {
    width: '280px',
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.customBorderRadius[1],
      height: '30px',
      '& fieldset': {
        borderColor: theme.palette.primary.borderGreyPrimary,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.mainHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const readOnlyStyle = {
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
    fontWeight: theme.typography.customFontWeight[1],
  };

  const showPasswordSetting = useMemo(() => {
    return securityTypeConfig[formData?.security]?.isPasswordRequired;
  }, [formData?.security]);

  const showRadiusProfileSetting = useMemo(() => {
    return securityTypeConfig[formData?.security]?.isRadiusProfileRequired;
  }, [formData?.security]);

  const isPasswordValid = useMemo(() => {
    let isValid = true;

    if (!formData) return isValid;

    const isPasswordRequiredSecurityMode =
      securityTypeConfig[formData?.security]?.isPasswordRequired;
    const isPasswordValidByRegexp = passwordRegex.wireless.ssid.test(
      formData?.password
    );

    if (isPasswordRequiredSecurityMode && !isPasswordValidByRegexp)
      isValid = false;
    else if (
      !isPasswordRequiredSecurityMode &&
      formData?.password &&
      !isPasswordValidByRegexp
    )
      isValid = false;

    return isValid;
  }, [formData?.security, formData?.password]);

  const handleStatusSwitch = () => {
    const newStatus = !isStatusSwitched;
    setIsStatusSwitched(newStatus);

    if (formData) {
      setFormData({
        ...formData,
        enable: newStatus ? true : false,
      });
    }
  };

  const handleSSIDBroadcastSwitch = () => {
    const newStatus = !isSSIDBroadcastSwitched;
    setIsSSIDBroadcastSwitched(newStatus);

    if (formData) {
      setFormData({
        ...formData,
        broadcastSSID: newStatus ? true : false,
      });
    }
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      return updated;
    });
  };

  const handleCloseModal = () => {
    setFormData(originalRowData);
    setHasDuplicateName(false);
  };

  const handleOpenRemoveModal = () => {
    setRemoveModal(true);
  };

  const handleOpenDisableModal = () => {
    setDisableWifiModal(true);
  };

  const onSave = () => {
    const newWirelessData = wirelessData.map(ssid => {
      if (ssid.id === formData.id) return { ...ssid, ...formData };
      else if (ssid.tmpCreateId && ssid.tmpCreateId === formData.tmpCreateId) return { ...ssid, ...formData };
      else return ssid;
    })

    setWirelessData(structuredClone(newWirelessData));
    onUpdateBlockerData(newWirelessData);

    setDrawerOpen(false);
    setShowPassword(false);

    setAlertInfo({
      open: true,
      severity: "success",
      message: (
        <Box>
          <Box
            sx={{
              fontSize: theme.typography.customFontSize[3],
              fontWeight: theme.typography.customFontWeight[3],
              pb: 1
            }}
          >
            Changes Saved
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography>Settings saved to draft. Click</Typography>
            <Typography sx={{ fontWeight: theme.typography.customFontWeight[3] }}> "Apply Changes" </Typography>
            <Typography>to deploy them to the device.</Typography>
          </Box>
        </Box>
      )
    });

  };

  const onRemove = () => {
    const newWirelessData = wirelessData.map(ssid => {
      if (ssid.id === formData.id) return { ...ssid, ...formData, isWaitingRemove: true };
      else if (ssid.tmpCreateId && ssid.tmpCreateId === formData.tmpCreateId) return { ...formData, isWaitingRemove: true }; // Deleting an unsaved SSIDs also need line-through.
      else return ssid;
    })

    setWirelessData(structuredClone(newWirelessData));
    onUpdateBlockerData(newWirelessData);

    setDrawerOpen(false);
    setRemoveModal(false)
    setShowPassword(false);
  };

  const isFormInvalid = () => {
    let isInvalid = false;

    if (!formData?.ssid || ssidNameSize < 1 || ssidNameSize > 32 || hasDuplicateName) isInvalid = true;
    if (!formData?.band?.length) isInvalid = true;
    if (!formData?.security) isInvalid = true;
    if (
      !formData?.vlanId ||
      formData?.vlanId < formLimitation.vlanId.min ||
      formData?.vlanId > formLimitation.vlanId.max
    )
      isInvalid = true;
    if (showPasswordSetting && !isPasswordValid) isInvalid = true;

    const isSupportRadiusProfile = ["WPA/WPA2-ENTERPRISE", "WPA2-ENTERPRISE", "WPA3-ENTERPRISE"].includes(formData.security);
    const hasRadiusProfile = radiusProfileOptions.find(item => item?.value === formData?.radiusProfile);
    if (isSupportRadiusProfile && !hasRadiusProfile) {
      isInvalid = true;
    }

    return isInvalid;
  };

  const getAvailableSecurities = useCallback(({ bands }) => {
    const has6G = bands.includes('6');

    if (bands.length === 0) {
      return securityTypeAssembleList;
    }

    if (has6G) {
      return securityTypeAssembleList.filter(s => SECURITY_ALLOWED_6G.includes(s.value));
    }

    return securityTypeAssembleList;
  }, []);

  useEffect(() => {
    const bands = formData?.band ?? [];
    const isMultipleBands = bands.length >= 2;
    const newSecurityList = getAvailableSecurities({ bands });
    setSecurityList(newSecurityList);

    setFormData(prev => {
      const updates = {};

      // Ensure mlo is disabled when multiple bands are not selected.
      if (!isMultipleBands) {
        updates.mloEnable = false;
      }

      // Ensure selected security is valid for the current band configuration.
      const securityValid = newSecurityList.some(s => s.value === prev.security);
      const nextSecurity = securityValid ? prev.security : newSecurityList[0]?.value ?? null;
      if (nextSecurity !== prev.security) {
        updates.security = nextSecurity;
      }

      // Disable MLO when security does not support it.
      const mloAllowed = SECURITY_ALLOWED_MLO.includes(nextSecurity);
      if (!mloAllowed && prev.mloEnable) {
        updates.mloEnable = false;
      }

      // Update wpaMode only when security changes.
      if (nextSecurity && nextSecurity !== prev.security) {
        const config = securityTypeConfig[nextSecurity];
        updates.wpaMode = config?.wpaMode?.display ?? null;
        updates.encryption = config?.encryption ?? null;
        updates.password = config?.isPasswordRequired ? prev.password : null;
      }

      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
  }, [formData?.band, formData?.security]);

  useEffect(() => {
    const newPmfState = PMF_MAP[formData?.wpaMode] ?? 'Disable';
    if (formData.pmfState !== newPmfState) {
      setFormData(prev => ({ ...prev, pmfState: newPmfState }));
    }
  }, [formData?.wpaMode]);

  useEffect(() => {
    if (!formData) return;

    setIsStatusSwitched(formData.enable === true);
    setIsSSIDBroadcastSwitched(formData.broadcastSSID === true);
    setShowPassword(false);
  }, [formData]);

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress sx={{ color: theme.palette.primary.mainHover }} />
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 2,
          px: 4,
          boxSizing: "border-box",
          gap: "20px",
        }}
      >
        <CollapseCard title="General">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {formData && (
              <>
                {/* Wi-Fi */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Wi-Fi
                  </Typography>
                  <Box
                    onClick={handleStatusSwitch}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      pointerEvents: showEditGeneral ? "auto" : "none",
                    }}
                  >
                    {formData?.enable === true || isStatusSwitched ? (
                      <SwitchOnIcon />
                    ) : (
                      <SwitchOffIcon />
                    )}
                  </Box>
                </Box>
                {/* Wi-Fi Name (SSID) */}
                <Box sx={rowStyle}>
                  <Box sx={{ paddingTop: hasDuplicateName ? '0px' : '10px', height: '52px' }}>
                    <Typography className={mainStyle["collapse-typography"]}>
                      Wi-Fi Name (SSID)
                    </Typography>
                  </Box>
                  <Box sx={labelStyle}>
                    <TextField
                      type="text"
                      autoComplete="off"
                      sx={textfieldStyle}
                      value={formData.ssid || ""}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          ssid: e.target.value,
                        })

                        const tmpTableData = wirelessData?.filter(item => item?.ssid !== originalRowData?.ssid);
                        const hasDuplicate = tmpTableData.some(item => item.ssid === e.target.value);
                        setHasDuplicateName(hasDuplicate);
                      }}
                      error={hasDuplicateName || (!isNil(formData?.ssid) && ( ssidNameSize < 1 || ssidNameSize > 32 ))}
                      helperText={hasDuplicateName ? 'This name already exists in this site.' : ''}
                      disabled={!showEditGeneral}
                    />
                  </Box>

                </Box>
                {/* Band */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Band
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {Object.keys(bandOptions).map((band, index) => (
                      <Box
                        key={band}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Checkbox
                          sx={{ paddingLeft: index === 0 ? '0px' : '9px' }}
                          checked={!!formData?.band?.includes(band)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const updatedBands = checked
                              ? [...formData.band, band]
                              : formData.band.filter((v) => v !== band)

                            setFormData({
                              ...formData,
                              band: updatedBands.sort((a, b) => a - b),
                            });

                            setBandCount(prev => ({
                              ...prev,
                              [band]: (prev[band] || 0) + (checked ? 1 : -1),
                            }));
                          }}
                          icon={!showEditGeneral || (!formData.band.includes(band) && bandCount[band] >= 8) ? <CheckDisabledIcon style={{ width: 24, height: 24, paddingTop: 2 }} /> : <UnselectedIcon />}
                          checkedIcon={<SelectedIcon />}
                          disableRipple
                          disabled={!showEditGeneral || (!formData.band.includes(band) && bandCount[band] >= 8)}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.primary.greyPrimary,
                            fontSize: theme.typography.customFontSize[2],
                          }}
                        >
                          {bandOptions[band]} GHz
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* MLO */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    MLO
                    <Tooltip
                      arrow
                      placement="bottom"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 330,
                          },
                        },
                      }}
                      title={
                        <Box>
                          <Typography variant="body2" fontSize={13}>
                            Enable Multi-Link Operation (MLO) to let Wi-Fi 7 clients connect across multiple bands and switch smoothly between links for improved performance and reliability.
                          </Typography>
                          <Typography fontWeight="bold" fontSize={13}>
                            Important
                          </Typography>
                          <Typography variant="body2" fontSize={13}>
                            MLO requires Wi-Fi 7 (802.11be) and MLO-capable client devices.
                          </Typography>
                          <Typography variant="body2" fontSize={13}>
                            The SSID enabled MLO only supports WPA3 or Enhanced Open clients.
                          </Typography>
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: 16,
                          marginLeft: 1,
                          '& img': {
                            content: `url(${icon_snmp})`,
                          },
                          '&:hover img': {
                            content: `url(${icon_snmp_hover})`,
                          },
                        }}
                      >
                        <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: 'pointer' }} />
                      </Box>
                    </Tooltip>
                  </Typography>
                  <Box
                    sx={{ width: 40, height: 22, display: 'flex', alignItems: 'center', pointerEvents: showEditGeneral ? "auto" : "none", }}
                    onClick={() => setFormData(prev => ({ ...prev, mloEnable: !prev?.mloEnable }))}
                  >
                    <IconButton
                      sx={{
                        paddingLeft: 0,
                        cursor: (formData?.band?.length < 2 || !SECURITY_ALLOWED_MLO.includes(formData?.security)) ? 'default' : 'pointer',
                      }}
                      onClick={(e) => (formData?.band?.length < 2 || !SECURITY_ALLOWED_MLO.includes(formData?.security)) && e.stopPropagation()}
                    >
                      {formData?.mloEnable ? <SwitchOnIcon /> : <SwitchOffIcon />}
                    </IconButton>
                  </Box>
                </Box>
                {/* Security */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Security
                  </Typography>
                  <MenuSelect
                    value={formData?.security || ''}
                    onChange={(opt) => {
                      const isOpenType = [
                        "OPEN",
                        "ENHANCE_OPEN",
                        "ENHANCE_OPEN+OPEN",
                      ].includes(opt.value);
                      const config = securityTypeConfig[opt.value] || {
                        wpaMode: "",
                        encryption: "",
                      };

                      setFormData((prev) => ({
                        ...prev,
                        security: opt.value,
                        wpaMode: isOpenType ? null : config.wpaMode.display,
                        encryption: isOpenType ? null : config.encryption,
                        password: config.isPasswordRequired ? prev.password : null,
                        radiusProfile: config.isRadiusProfileRequired ? radiusProfileOptions?.[0]?.value : null,
                      }));
                    }}
                    options={securityList.filter((security) => security.isShow)}
                    width="100%"
                    disabled={!showEditGeneral}
                  />
                </Box>
                {/* RADIUS Profile */}
                {showRadiusProfileSetting && (
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]}>
                      RADIUS Profile
                    </Typography>
                    <MenuSelect
                      value={formData?.radiusProfile ?? ''}
                      options={radiusProfileOptions}
                      width="100%"
                      onChange={(opt) => {
                        setFormData((prev) => ({
                          ...prev,
                          radiusProfile: opt?.value ?? null,
                        }));
                      }}
                    />
                  </Box>
                )}
                {/* PMF */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    PMF
                  </Typography>
                  <Typography sx={readOnlyStyle}>{formData?.pmfState || 'Disable'}</Typography>
                </Box>
                {/* WPA Mod */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    WPA Mode
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    {formData.wpaMode || "-"}
                  </Typography>
                </Box>
                {/* Encryption Type */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Encryption Type
                  </Typography>
                  <Typography sx={readOnlyStyle}>
                    {formData.encryption || "-"}
                  </Typography>
                </Box>
                {/* Wi-Fi Password */}
                {showPasswordSetting && (
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]}>
                      Wi-Fi Password
                    </Typography>
                    <TextField
                      type={showPassword ? "text" : "password"}
                      value={formData.password || ""}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      sx={isValidInputStyles(
                        formData?.password == null || isPasswordValid
                      )}
                      error={!isPasswordValid}
                      placeholder="Enter Password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={toggleShowPassword}
                              edge="end"
                              sx={{
                                padding: 1,
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.primary.transparent,
                                },
                                "&.Mui-focusVisible": {
                                  backgroundColor:
                                    theme.palette.primary.transparent,
                                },
                              }}
                            >
                              {showPassword ? <ShowIcon /> : <HideIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      disabled={!showEditGeneral}
                    />
                  </Box>
                )}

                {showPasswordSetting &&
                  !!formData.password &&
                  !isPasswordValid && (
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                        marginLeft: "10.3rem",
                        marginTop: "-1rem",
                      }}
                    >
                      Minimum length: 8 characters. Allowed symbols: ~ ! [ ] ; ' \ : @ # $ & *
                    </Typography>
                  )}
                {/* VLAN */}
                <Box sx={rowStyle}>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    VLAN
                  </Typography>
                  <TextField
                    type="number"
                    value={formData.vlanId || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        vlanId: +e.target.value,
                      })
                    }
                    sx={isValidInputStyles(formData?.vlanId >= formLimitation.vlanId.min && formData?.vlanId <= formLimitation.vlanId.max)}
                    helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                    disabled={!showEditGeneral}
                  />
                </Box>
              </>
            )}
          </Box>
        </CollapseCard>
        <CollapseCard title="Advanced Settings">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {formData && (
              <>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    SSID Broadcast
                  </Typography>
                  <Box
                    onClick={handleSSIDBroadcastSwitch}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      pointerEvents: showEditGeneral ? "auto" : "none",
                    }}
                  >
                    {formData?.broadcastSSID === true ||
                      isSSIDBroadcastSwitched ? (
                      <SwitchOnIcon />
                    ) : (
                      <SwitchOffIcon />
                    )}
                  </Box>
                </Box>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Captive Portal
                  </Typography>
                  <MenuSelect
                    value={formData?.captivePortalName ?? "disabled"}
                    options={captivePortalOptions}
                    width="100%"
                    disabled={!showEditGeneral}
                    onChange={(opt) => {
                      setFormData((prev) => ({
                        ...prev,
                        captivePortalName:
                          opt?.value === "disabled" ? null : opt?.value ?? null,
                      }));
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </CollapseCard>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            width: "100%",
            marginBottom: "120px",
          }}
        >
          <Button
            onMouseEnter={() => setIsDeleteHovered(true)}
            onMouseLeave={() => setIsDeleteHovered(false)}
            onClick={handleOpenRemoveModal}
            startIcon={isDeleteHovered ? <BinHoverIcon /> : <BinIcon />}
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[3],
              fontFamily: theme.typography.fontFamily,
              backgroundColor: theme.palette.primary.transparent,
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
                color: theme.palette.primary.mainHover,
              },
              "&:active": {
                backgroundColor: theme.palette.primary.transparent,
                color: theme.palette.primary.main,
              },
              "&:disabled": {
                color: theme.palette.action.disabled,
              },
            }}
            disabled={!showEditGeneral}
          >
            Remove Network
          </Button>
        </Box>

        {originalRowData &&
          JSON.stringify(originalRowData) !==
          JSON.stringify(formData) && (
            <>
              <Box
                sx={{
                  position: "sticky",
                  width: "106%",
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  padding: "16px",
                  backgroundColor: "primary.lightBlue",
                  bottom: 0,
                }}
              >
                <OutlineButton
                  onClick={handleCloseModal}
                  sx={{ marginRight: "20px" }}
                  width="100px"
                  height="32px"
                >
                  Cancel
                </OutlineButton>
                <SolidButton
                  disabled={isFormInvalid()}
                  onClick={() => {
                    if (formData?.enable === false) {
                      handleOpenDisableModal();
                    } else {
                      onSave();
                    }
                  }}
                  width="100px"
                  height="32px"
                >
                  Save
                </SolidButton>
              </Box>
            </>
          )}
        {/* Remove Modal */}
        <ModalContainer display={removeModal} modalWidth="500px">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontFamily: theme.typography.fontFamily,
                fontWeight: theme.typography.customFontWeight[3],
                fontSize: theme.typography.customFontSize[5],
              }}
            >
              Remove {formData?.ssid}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.primary.black,
                  fontSize: theme.typography.customFontSize[3],
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.customFontWeight[0],
                  whiteSpace: "pre-wrap",
                }}
              >
                Are you sure you want to remove "{formData?.ssid}"?
              </Typography>
            </Box>

            <Box
              sx={{
                paddingTop: "20px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <OutlineButton
                onClick={() => setRemoveModal(false)}
                sx={{ marginRight: "20px" }}
                width="100px"
                height="32px"
              >
                Cancel
              </OutlineButton>
              <SolidWarnButton onClick={onRemove} width="100px" height="32px">
                Remove
              </SolidWarnButton>
            </Box>
          </Box>
        </ModalContainer>

        <ModalContainer display={disableWifiModal} modalWidth="500px">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontFamily: theme.typography.fontFamily,
                fontWeight: theme.typography.customFontWeight[3],
                fontSize: theme.typography.customFontSize[5],
              }}
            >
              Disable {formData?.ssid} ?
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.primary.black,
                  fontSize: theme.typography.customFontSize[3],
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.customFontWeight[0],
                  whiteSpace: "pre-wrap",
                }}
              >
                Disabling "{formData?.ssid}" WI-Fi will disconnect all
                clients connected to this Wi-Fi. Are you sure you want to
                proceed?
              </Typography>
            </Box>

            <Box
              sx={{
                paddingTop: "20px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <OutlineButton
                onClick={() => setDisableWifiModal(false)}
                sx={{ marginRight: "20px" }}
                width="100px"
                height="32px"
              >
                Cancel
              </OutlineButton>
              <SolidButton
                onClick={() => {
                  setDisableWifiModal(false);
                  onSave();
                }}
                width="100px"
                height="32px"
              >
                Disable
              </SolidButton>
            </Box>
          </Box>
        </ModalContainer>
      </Box>

    </>
  );
};
export default SettingsDrawer;
